import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { History } from "lucide-react";
import { api } from "@/lib/api";
import { RelativeTime } from "@/components/RelativeTime";

interface AuditEvent {
  id: string;
  ts: string;
  source: "audit_events" | "env_audit";
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  projectId: string | null;
  payload: Record<string, unknown>;
}

/**
 * Per-project activity feed — same audit_events/env_audit union as the org
 * Audit log page, scoped server-side to this project via
 * GET .../projects/:project/audit-events.
 */
export function ProjectActivity() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const [action, setAction] = useState<string>("");

  const q = useQuery({
    queryKey: ["project-audit", org, project, action],
    queryFn: () =>
      api.get<{ events: AuditEvent[] }>(
        `/orgs/${org}/projects/${project}/audit-events?limit=100${action ? `&action=${encodeURIComponent(action)}` : ""}`,
      ),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold inline-flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" /> Activity
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Recent changes to this project. Read-only; both source tables are append-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-2xs text-subtle-foreground" htmlFor="action-filter">
            Filter
          </label>
          <select
            id="action-filter"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="h-8 px-2 rounded-md border border-border bg-background text-xs font-mono max-w-full"
          >
            <option value="">All actions</option>
            <option value="deployment.rolled_back">deployment.rolled_back</option>
            <option value="deployment.promoted">deployment.promoted</option>
            <option value="deploy_hook.fired">deploy_hook.fired</option>
            <option value="deploy_hook.created">deploy_hook.created</option>
            <option value="deploy_hook.revoked">deploy_hook.revoked</option>
            <option value="deploy_hook.regenerated">deploy_hook.regenerated</option>
            <option value="protection.level_changed">protection.level_changed</option>
            <option value="protection.password_rotated">protection.password_rotated</option>
            <option value="protection.bypass_secret_rotated">protection.bypass_secret_rotated</option>
            <option value="shareable_link.created">shareable_link.created</option>
            <option value="shareable_link.revoked">shareable_link.revoked</option>
            <option value="domain.added">domain.added</option>
            <option value="domain.verified">domain.verified</option>
            <option value="domain.assigned">domain.assigned</option>
            <option value="domain.reassigned">domain.reassigned</option>
            <option value="domain.removed">domain.removed</option>
            <option value="domain.failed">domain.failed</option>
            <option value="project_member.granted">project_member.granted</option>
            <option value="project_member.role_changed">project_member.role_changed</option>
            <option value="project_member.revoked">project_member.revoked</option>
          </select>
        </div>
      </div>

      <div className="of-card overflow-hidden">
        {q.isLoading && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {q.data?.events.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No activity yet{action ? ` for action "${action}"` : ""}.
          </div>
        )}
        {q.data?.events.map((e) => (
          <div key={e.id} className="of-row flex-wrap gap-3 items-start">
            <span
              className={
                "text-2xs uppercase tracking-wider font-mono border rounded px-1.5 py-0.5 " +
                (e.source === "env_audit"
                  ? "bg-attention-subtle text-attention border-attention/30"
                  : "bg-accent-subtle text-accent border-accent/30")
              }
            >
              {e.action}
            </span>
            <div className="min-w-0 w-full order-last sm:w-auto sm:order-none sm:flex-1">
              <pre className="text-2xs text-muted-foreground whitespace-pre-wrap break-words font-mono leading-relaxed">
                {JSON.stringify(e.payload, null, 0)}
              </pre>
            </div>
            <div className="text-2xs text-subtle-foreground whitespace-nowrap">
              {e.actorEmail ?? "system"}
            </div>
            <div className="text-2xs text-subtle-foreground tabular-nums whitespace-nowrap ml-auto sm:ml-0">
              <RelativeTime value={e.ts} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
